import {toLower} from '@servicenow/ui-internal';
import {
	createStyleFragment,
	getHostVariables,
	styleSheetsContentCache,
	resolveStylesheetsContent
} from './utils';
import {VERSION, ROOT_NODE} from './symbols';

const view = () => null;
const stylesMap = new Map();

export default async function applyStyles(el, config) {
	const {
		styles = '',
		stylesheets = [],
		renderer: {type: {onStateChange = () => null, deprioritizeStyles = false} = {}} = {}
	} = config;

	// eslint-disable-next-line nonblock-statement-body-position
	if (!styles && !stylesheets.length) return;

	const hostVariables = getHostVariables(el);
	const rootNode = hostVariables[ROOT_NODE];
	const {tagName} = el;

	if (
		Document &&
		'adoptedStyleSheets' in Document.prototype &&
		(process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test')
	)
		return applyWithAdoptedStylesSheets(el, tagName, rootNode, styles, stylesheets);

	applyWithTag(el, tagName, rootNode, styles, stylesheets, deprioritizeStyles, onStateChange);
}

async function applyWithAdoptedStylesSheets(el, tagName, rootNode, styles = '', stylesheets) {
	if (!stylesMap.has(tagName)) {
		const externalStyles = document.head.querySelector(`#now-${toLower(tagName)}-styles`);
		if (externalStyles) {
			const styleEl = externalStyles.content.cloneNode(true).querySelector('style');
			styles = styleEl ? styleEl.textContent : styles; // eslint-disable-line no-param-reassign
		}

		let stylesheetsContent = '';

		if (stylesheets && stylesheets.length) {
			stylesheetsContent = styleSheetsContentCache.get(el, tagName);
			if (!stylesheetsContent) {
				const previousDisplaValue = el.style.display;
				el.style.setProperty('display', 'none', 'important');
				stylesheetsContent = await resolveStylesheetsContent(stylesheets);
				el.style.setProperty('display', previousDisplaValue);
			}
		}

		const sheet = new CSSStyleSheet();
		sheet.replace(`${styles} ${stylesheetsContent}`);
		stylesMap.set(tagName, [sheet]);
	}

	rootNode.adoptedStyleSheets = stylesMap.get(tagName); // eslint-disable-line no-param-reassign
}

async function applyWithTag(
	el,
	tagName,
	rootNode,
	styles,
	stylesheets,
	deprioritizeStyles,
	onStateChange
) {
	let stylesheetsContent = '';

	// eslint-disable-next-line nonblock-statement-body-position
	if (deprioritizeStyles) onStateChange(rootNode, view);

	if (stylesheets && stylesheets.length) {
		const version = getHostVariables(VERSION) || '';
		stylesheetsContent = styleSheetsContentCache.get(version, tagName);
		if (!stylesheetsContent) {
			const previousDisplaValue = el.style.display;
			el.style.setProperty('display', 'none', 'important');
			stylesheetsContent = await resolveStylesheetsContent(stylesheets);
			el.style.setProperty('display', previousDisplaValue);
			if (stylesheetsContent) styleSheetsContentCache.set(version, tagName, stylesheetsContent);
		}
	}

	const styleFragment = createStyleFragment(styles, stylesheetsContent, tagName);
	rootNode.appendChild(styleFragment);
}
